import React, { useState, useEffect, useMemo } from "react";
import CMS_CONFIG from "./config/cmsSites";
import { VALID_STATES } from "./config/cmsSites";
import './translation.css';
import TextViewer from '../TextViewer/TextViewer';

function Translation() {
    const defaultCMS = CMS_CONFIG.GUJARATI || '';
    const [selectedCMS, setSelectedCMS] = useState(defaultCMS);
    const [selectedState, setSelectedState] = useState('ALL');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize] = useState(10);
    const [modalContent, setModalContent] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedCMS) {
                setLoading(true);
                setError(null);
                try {
                    const stateQuery = selectedState === 'ALL' ? '' : `&STATE=${selectedState}`;
                    const response = await fetch(`https://adbot.nw18.com/api/wp/translated-content?CMS-SITE=${selectedCMS}${stateQuery}&page_size=${pageSize}&page=${currentPage}`, {
                        method: 'GET',
                        headers: {
                            'X-AP-TOKEN': '4F687754D3A64', 
                            'Content-Type': 'application/json',
                        },
                    });
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                    const result = await response.json();
                    if (result.success) {
                        setData(result.data || []);
                        setTotalPages(result.pagination.totalPage || 0);
                    } else {
                        setData([]);
                        setTotalPages(0);
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                    setError('Failed to fetch data. Please try again later.');
                    setData([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setData([]);
                setTotalPages(0);
            }
        };

        fetchData();
    }, [selectedCMS, selectedState, currentPage, pageSize]);

    const cmsOptions = useMemo(() => 
        Object.keys(CMS_CONFIG).map(key => (
            <option key={key} value={CMS_CONFIG[key]}>
                {key}
            </option>
        )), []);

    const stateOptions = useMemo(() => 
        ['ALL', ...VALID_STATES].map(state => (
            <option key={state} value={state}>
                {state}
            </option>
        )), []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleSeeMore = (content, title) => {
        setModalContent(content);
        setModalTitle(title);
        setIsModalOpen(true);
    };

    const renderText = (text, title) => {
        const displayText = text || ''; 
        return displayText.length > 200 ? (
            <>
                <div dangerouslySetInnerHTML={{ __html: displayText.slice(0, 200) }} />
                <button onClick={() => handleSeeMore(displayText, title)}>See More</button>
            </>
        ) : (
            <div dangerouslySetInnerHTML={{ __html: displayText }} />
        );
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <>
            <section className="translation-header">
                <h1>Translation</h1>
                <div className="translation-dropdown">
                    <label htmlFor="cms-select">CMS SITES:</label>
                    <select id="cms-select" onChange={e => setSelectedCMS(e.target.value)} value={selectedCMS}>
                        {cmsOptions}
                    </select>
                    <label htmlFor="state-select">STATE:</label>
                    <select id="state-select" onChange={e => setSelectedState(e.target.value)} value={selectedState}>
                        {stateOptions}
                    </select>
                </div>
            </section>
            
            <section className="translation-table">
                {loading ? (
                    <p>Loading data...</p>
                ) : error ? (
                    <p className="error">Error: {error}</p>
                ) : data.length > 0 ? (
                    <>
                        <div className="table-container">
                            <div className="table-wrapper">
                                <table className="translation-table">
                                    <thead>
                                        <tr>
                                            <th>State</th>
                                            <th className="translation-para">Input text</th>
                                            <th className="translation-para">Translated text</th>
                                            <th className="translation-para">Edited text</th>
                                            <th className="translation-para">Published text</th>
                                            <th>Email id</th>
                                            <th>Detected language</th>
                                            <th>Output language</th>
                                            <th>Feedback Message</th>
                                            <th>Discard Message</th>
                                            <th>Created At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.state}</td>
                                                <td>{renderText(item.input_text, 'Input Text')}</td>
                                                <td>{renderText(item.translated_text, 'Translated Text')}</td>
                                                <td>{renderText(item.edited_text, 'Edited Text')}</td>
                                                <td>{renderText(item.published_text, 'Published Text')}</td>
                                                <td>{item.email_id}</td>
                                                <td>{item.detected_language}</td>
                                                <td>{item.output_language}</td>
                                                <td>{item.feedback_message}</td>
                                                <td>{item.discard_message}</td>
                                                <td>{item.created_at ? formatDate(item.created_at) : " "}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="pagination-controls">
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)} 
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button 
                                onClick={() => handlePageChange(currentPage + 1)} 
                                disabled={currentPage === totalPages || totalPages === 0}
                            >
                                Next
                            </button>
                        </div>
                    </>
                ) : (
                    <p>No data available</p>
                )}
            </section>

            {/* Render TextViewer Modal */}
            <TextViewer 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                content={modalContent} 
                title={modalTitle} 
            />
        </>
    );
}

export default Translation;
